import { yieldToMain } from 'assets/core/js/common'
import multivariateTesting from 'assets/core/js/module/multivariateTesting/multivariateTesting'

type Data = Record<string, object | unknown[] | number | string>

export interface PageData {
  getDataForTag(gtmId: string): Data
  addData(tagId: string, pageData: Data): void
}

export type DataLayer = Record<string | number, unknown> | IArguments | false
type TransformElement = (data: Data, el: Element, domEvent: Event | null) => DataLayer
type TransformElements = (data: Data, el: Element[], domEvent: Event | null) => DataLayer
type Elements = NodeListOf<Element> | Element[] | Element

declare global {
  interface Window {
    pageDataLayer: Record<string, Array<DataLayer>>
    pageDataLayerCache: Record<string, Array<DataLayer>>
    pageData: PageData
    hasConsentMode: boolean
  }
}

interface TagData {
  [key: string]: {
    initialState: [{ data: unknown }]
    pageData: Record<string, unknown>
  }
}

interface TagDatas {
  gtm: TagData
}

const dataLayer = {
  hasResponseToConsent: false,

  pushInLayer(gtmId: string, data: DataLayer, byPassResponseToConsent = false) {
    if (window.pageDataLayer[gtmId] && data) {
      for (const key in data) {
        if (data[key] === undefined) {
          delete data[key]
        }
      }

      data = this.overrideData(data)

      if (byPassResponseToConsent || this.hasResponseToConsent) {
        void (async () => {
          await yieldToMain()
          window.pageDataLayer[gtmId]?.push(data)
        })()
      } else {
        window.pageDataLayerCache[gtmId]?.push(data)
      }
    }
  },

  pushOnElementClick(gtmId: string, element: Elements, transform: TransformElement) {
    if (!transform) {
      return
    }
    const elements = this.normalizeElements(element)
    elements.forEach((el) => {
      el.addEventListener('click', (domEvent) => {
        this.pushInLayer(gtmId, transform(this.getData(gtmId), el, domEvent))
      })
    })
  },

  pushOnElementSubmit(gtmId: string, element: Elements, transform: TransformElement) {
    if (!transform) {
      return
    }
    const elements = this.normalizeElements(element)
    elements.forEach((element) => {
      const el = element.querySelector('[type="submit"]')

      if (el) {
        el.addEventListener('click', (domEvent) => {
          this.pushInLayer(gtmId, transform(this.getData(gtmId), el, domEvent))
        })
      }
    })
  },

  pushOnCustomEvent(gtmId: string, element: EventTarget, customEvent: string, transform: TransformElement) {
    if (!element || !customEvent || !transform) {
      return
    }

    element.addEventListener(customEvent, (domEvent) => {
      this.pushInLayer(gtmId, transform(this.getData(gtmId), element as Element, domEvent))
    })
  },

  pushOnElementVisible(gtmId: string, element: Elements, transform: TransformElements, threshold = 0.5) {
    const elements = this.normalizeElements(element)
    if (elements.length === 0 || !transform) {
      return
    }

    const callback: IntersectionObserverCallback = (entries, observer) => {
      const els = entries.filter((entry) => entry.isIntersecting).map((entry) => entry.target)
      if (!els.length) {
        return
      }
      this.pushInLayer(gtmId, transform(this.getData(gtmId), els, null))
      els.forEach((elt) => {
        observer.unobserve(elt)
      })
    }

    const observer = new IntersectionObserver(callback, { threshold })
    elements.forEach((el) => {
      observer.observe(el)
    })
  },

  pushForJsonResponse(tagDatas: TagDatas) {
    if (!tagDatas || !tagDatas.gtm) {
      return
    }

    const tagDatasGtm: TagData = tagDatas.gtm
    for (const tagId in tagDatasGtm) {
      tagDatasGtm[tagId]?.initialState.forEach((data: Record<string, unknown>) => {
        this.pushInLayer(tagId, data)
      })
      // @ts-expect-error object is defined
      if (tagDatasGtm[tagId].pageData) {
        // @ts-expect-error object is defined
        window.pageData.addData(tagId, tagDatasGtm[tagId].pageData)
      }
    }
  },

  onResponseToConsent() {
    this.hasResponseToConsent = true
    // We have a response to consent, we push data from cache to layer
    for (const gtmId in window.pageDataLayerCache) {
      const data = window.pageDataLayerCache[gtmId]
      if (data && data.length > 0) {
        for (let i = 0; i < data.length; i++) {
          this.pushInLayer(gtmId, data[i] || false)
        }
        window.pageDataLayerCache[gtmId] = []
      }
    }
  },

  normalizeElements(element: NodeListOf<Element> | Element[] | Element): Element[] {
    if (!element) {
      return []
    }

    // @ts-expect-error if needed forEach will be available
    if (typeof element.forEach === 'function') {
      return element as Element[]
    }

    // @ts-expect-error if needed iterator will be available
    if (typeof element[Symbol.iterator] === 'function') {
      // @ts-expect-error if needed iterator will be available
      return [...element]
    }

    return element ? [element as Element] : []
  },

  getData(gtmId: string): Data {
    return window.pageData.getDataForTag(gtmId)
  },

  /**
   * page_view event is an initial event created by the backend. The data of this event is therefore stored within
   * the HTML of the page, and it may be cached. To reduce the number of objects in cache, multivariate groups
   * are not stored with the other data of the page_view event. Instead, there are added on the fly with
   * the data coming from the multivariateGroups cookie.
   */
  overrideData(data: DataLayer): DataLayer {
    if (typeof data === 'object' && 'event' in data && data.event === 'page_view') {
      Object.assign(data, multivariateTesting.getAllMultivariateTestingValues())
    }

    return data
  },

  getPageCategory(gtmId: string): string | undefined {
    const pageCategory = this.getData(gtmId).page_category

    return typeof pageCategory === 'string' ? pageCategory : undefined
  },

  events: {
    SEARCH_VIEW_PRODUCTS: 'view_item_list',
    SEARCH_SELECT_PRODUCT: 'select_item',
    SEARCH_USE_FILTER: 'use_filter',
    PRODUCT_VIEW_ACCOMMODATIONS: 'view_item_list',
    PRODUCT_SELECT_ACCOMMODATION: 'select_item',
    PRODUCT_BEGIN_CHECKOUT: 'begin_booking',
    CHECKOUT_SELECT_PAYMENT_METHOD: 'begin_payment',
    CHECKOUT_SECTION_TRAVELLERS: 'view_guests_details',
    CHECKOUT_SECTION_OPTIONS: 'view_stay_options',
    FAVORITE_ADDED: 'add_to_favorite',
    FAVORITE_REMOVED: 'remove_from_favorite',
    CUSTOMER_ZONE_VOUCHER_DOWNLOAD: 'download_voucher',
    SEARCH: 'search',
    FILTER: 'filter',
    // Event names for old tagguage
    OLD_PRODUCT_CHECK_OFFER: 'CheckOffer',
    OLD_HOMEPAGE_CMS: 'homeClick',
    SEARCH_MAP_OPEN: 'view_map',
    SEARCH_MAP_CLOSE: 'close_map',
    SEARCH_MAP_MOVE: 'move_map',
    SEARCH_MAP_SELECT_PRODUCT: 'pin_map',
    ACCOMMODATION_PLAN_VIEW: 'view_accommodation_plan',
  },

  // Keys to get choice for consent mode
  consentMode: {
    googleAnalytics: 'google_analytics',
  },
}

export default dataLayer
